import { Component, Inject, Input, OnInit, WritableSignal, signal } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PersonaType, TaskManagerSdkService } from '@vendasta/task-manager';
import { EMPTY, Observable } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { ProjectTrackerServiceInterface, ProjectTrackerServiceInterfaceToken } from '../../project-tracker-interface';
import { AttachmentInterface, Subtask } from '../../shared/interfaces';
import { FileInfo, FileUploadError } from '@vendasta/galaxy/uploader';
import { TaskStatus } from '../../shared/constants';
import { TaskAttachmentService, TaskIdentity, TaskSdkService } from '@vendasta/task';

type LoadingState = { index: number; loading: boolean; subtaskId?: string } | undefined;

@Component({
  selector: 'project-tracker-fulfillment-subtasks',
  templateUrl: './fulfillment-subtasks.component.html',
  styleUrls: ['./fulfillment-subtasks.component.scss'],
})
export class FulfillmentSubtasksComponent implements OnInit {
  @Input() subtasks: Subtask[] = [];
  COMPLETED_STATUS = TaskStatus.COMPLETED;
  PersonaType = PersonaType;
  TEN_MB = 10 * 1024 * 1024;
  attachmentLoading: WritableSignal<LoadingState> = signal(undefined);
  deleteAttachmentLoading: WritableSignal<LoadingState> = signal(undefined);
  feedbackSaving: WritableSignal<LoadingState> = signal(undefined);
  approveSaving: WritableSignal<LoadingState> = signal(undefined);
  undoApproveSaving: WritableSignal<LoadingState> = signal(undefined);
  userPersonaAsString$: Observable<string>;

  constructor(
    @Inject(ProjectTrackerServiceInterfaceToken) private projectTrackerService: ProjectTrackerServiceInterface,
    private snackbarService: SnackbarService,
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private attachmentService: TaskAttachmentService,
    private taskManagerSdkService: TaskManagerSdkService,
  ) {}

  ngOnInit(): void {
    this.subtasks
      ?.map((subtask: Subtask) => {
        if (subtask?.feedback && !subtask?.conversations) {
          return {
            ...subtask,
            conversations: subtask.feedback.map((message) => ({ message })),
          };
        }
        return subtask;
      })
      ?.forEach((subtask: Subtask, index: number) => {
        const feedbackControlName = this.getFeedbackControlName(index);
        const controls = {};
        controls[feedbackControlName] = '';
        subtask.form = this.fb.group(controls);
      });
    this.userPersonaAsString$ = this.projectTrackerService.activeUser$.pipe(
      map((user) => user?.personaType?.toString()),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  getFeedbackControlName(index: number): string {
    return 'feedback' + index;
  }

  onFileError(): void {
    this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.FAIL_TO_ADD');
  }

  approveTask(index: number): void {
    this.approveSaving.set({ index, loading: true });
    const subtask = this.subtasks[index];
    subtask.showFeedback = false;
    subtask.approved = true;
    this.projectTrackerService
      .approveTask(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId)
      .pipe(
        take(1),
        tap(() => this.approveSaving.set(undefined)),
        switchMap(() =>
          this.snackbarService
            .openSuccessSnack('PROJECT_TRACKER.SUCCESS.APPROVE_TASK', {
              action: this.translateService.instant('PROJECT_TRACKER.UNDO'),
            })
            .onAction(),
        ),
        tap(() => this.undoApproveTask(subtask)),
        catchError(() => {
          this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.APPROVE_TASK');
          subtask.approved = false;
          this.approveSaving.set(undefined);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  undoApproveTask(subtask: Subtask): void {
    this.undoApproveSaving.set({ index: this.subtasks.indexOf(subtask), loading: true });
    subtask.approved = false;
    this.projectTrackerService
      .undoApproveTask(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.APPROVE_TASK_UNDO');
          this.undoApproveSaving.set(undefined);
        },
        error: () => {
          this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.APPROVE_TASK_UNDO');
          subtask.approved = true;
          this.undoApproveSaving.set(undefined);
        },
      });
  }

  saveFeedback(index: number): void {
    this.feedbackSaving.set({ index, loading: true });
    const subtask = this.subtasks[index];
    subtask.showFeedback = false;
    subtask.approved = false;
    if (!subtask?.conversations?.length) {
      subtask.conversations = [];
    }
    const feedback = subtask.form.get(this.getFeedbackControlName(index)).value;

    this.projectTrackerService
      .sendFeedback(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, feedback)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.snackbarService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FEEDBACK_PROVIDED');
          subtask.form.get(this.getFeedbackControlName(index)).patchValue('');
          this.feedbackSaving.set(undefined);
          subtask.conversations.push({
            message: feedback,
            sendTo: 'Digital Agent',
            source: 'smb',
            created: new Date(),
            isPublic: true,
          });
        },
        error: () => {
          this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.FEEDBACK_PROVIDED');
          subtask.conversations.pop();
          this.feedbackSaving.set(undefined);
        },
      });
  }

  handleFileDeleted(subtask: Subtask, attachmentToRemove: AttachmentInterface, indexSetLoading: number): void {
    this.deleteAttachmentLoading.set({ index: indexSetLoading, loading: true });
    this.projectTrackerService
      .removeFile(subtask.partnerId, subtask.accountGroupId, subtask.subtaskId, attachmentToRemove)
      .pipe(take(1))
      .subscribe({
        next: () => {
          const index = subtask.files.findIndex((file) => file === attachmentToRemove);
          if (index !== -1) {
            subtask.files.splice(index, 1);
            subtask.files = [...subtask.files];
          }
          this.snackbarService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FILE_REMOVE');
          this.deleteAttachmentLoading.set(undefined);
        },
        error: () => {
          this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.FILE_REMOVE');
          this.deleteAttachmentLoading.set(undefined);
        },
      });
  }

  handleFileUploaded(fileInfo: FileInfo, subtask: Subtask): void {
    this.snackbarService.openSuccessSnack('PROJECT_TRACKER.SUCCESS.FILE_UPLOAD');
    this.userPersonaAsString$.pipe(take(1)).subscribe((userPersona) => {
      if (!subtask.files) subtask.files = [];
      subtask.files = [
        ...subtask.files,
        {
          name: fileInfo?.name,
          url: fileInfo?.url,
          contentType: fileInfo?.file?.type,
          filePermissions: {
            read: [userPersona],
            delete: [userPersona],
            write: [userPersona],
          },
          uploadingPersonaType: userPersona,
        },
      ];
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  handleFileUploadErrored(error: FileUploadError): void {
    this.snackbarService.openErrorSnack('PROJECT_TRACKER.ERROR.FAIL_TO_ADD');
  }

  handleFileNameClicked(file: AttachmentInterface, subtask: Subtask, index: number): void {
    this.attachmentLoading.set({ index: index, subtaskId: subtask.subtaskId, loading: true });
    const secret = this.attachmentService.getSecretFromURL(file.url);

    const namespace = TaskSdkService.buildAccountGroupNamespace(subtask.partnerId, subtask.accountGroupId);
    // subtaskID is in format "/<parentTaskId>/<subtaskId>  "
    const subtaskIdParts: string[] = subtask.subtaskId.split('/');
    if (subtaskIdParts?.length !== 3) {
      this.snackbarService.openErrorSnack('There was an error downloading the file.');
      return;
    }
    const parentPath = '/' + subtaskIdParts[1] + '/';
    const taskId = subtaskIdParts[2];

    this.taskManagerSdkService
      .getAttachmentUrl(new TaskIdentity({ namespace, parentPath, taskId }), secret)
      .pipe(
        map((resp) => resp?.url),
        take(1),
        finalize(() => this.attachmentLoading.set({ index: index, subtaskId: '', loading: false })),
      )
      .subscribe((generatedUrl) => {
        try {
          const anchor = document.createElement('a');
          anchor.href = generatedUrl;
          anchor.download = file.name;
          anchor.target = '_blank';
          anchor.click();
          window.URL.revokeObjectURL(generatedUrl);
        } catch (e) {
          this.snackbarService.openErrorSnack('There was an error downloading the file.');
        }
      });
  }
}
